import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { User, Package, MapPin, Heart, Settings, LogOut, Plus, Trash2, Star } from "lucide-react";
import { useLang, useL } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { useWishlist } from "@/context/WishlistContext";
import { api, money, apiError } from "@/lib/api";
import { ProductCard, Stars } from "@/components/shared";
import { toast } from "sonner";

const inputCls = "w-full rounded-xl border border-border bg-cream px-4 py-3 text-sm outline-none focus:border-gold";
const statusColor = { pending: "bg-amber-100 text-amber-700", paid: "bg-blue-100 text-blue-700", processing: "bg-indigo-100 text-indigo-700", shipped: "bg-purple-100 text-purple-700", delivered: "bg-green-100 text-green-700", cancelled: "bg-red-100 text-red-700", refunded: "bg-gray-200 text-gray-700" };
const paymentStatusColor = { paid: "bg-green-100 text-green-700", pending: "bg-amber-100 text-amber-700", payment_pending: "bg-amber-100 text-amber-700", payment_failed: "bg-red-100 text-red-700" };
const paymentStatusLabel = { paid: { fr: "Payée", en: "Paid" }, pending: { fr: "En attente de paiement", en: "Awaiting payment" }, payment_pending: { fr: "En attente de paiement", en: "Awaiting payment" }, payment_failed: { fr: "Paiement échoué", en: "Payment failed" } };

export default function Account() {
  const { lang, t } = useLang();
  const L = useL();
  const { user, logout, setUser } = useAuth();
  const { ids } = useWishlist();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const tab = params.get("tab") || "profile";

  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [wishProducts, setWishProducts] = useState([]);
  const [profile, setProfile] = useState({ name: user?.name || "", phone: user?.phone || "" });
  const [addr, setAddr] = useState({ label: "Home", full_name: "", line1: "", line2: "", city: "", state: "", zip: "", phone: "", is_default: false });
  const [showAddr, setShowAddr] = useState(false);
  const highlightId = params.get("highlight");

  useEffect(() => {
    if (tab === "orders" && highlightId) {
      const el = document.getElementById(`order-${highlightId}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [tab, orders, highlightId]);

  useEffect(() => {
    if (tab === "orders") api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
    if (tab === "addresses") api.get("/addresses").then((r) => setAddresses(r.data)).catch(() => {});
    if (tab === "wishlist" && ids.length) api.get("/products?limit=100").then((r) => setWishProducts(r.data.items.filter((p) => ids.includes(p.id)))).catch(() => {});
    if (tab === "wishlist" && !ids.length) setWishProducts([]);
  }, [tab, ids]);

  const menu = [
    { key: "profile", icon: User, label: lang === "fr" ? "Mon Profil" : "My Profile" },
    { key: "orders", icon: Package, label: lang === "fr" ? "Mes Commandes" : "My Orders" },
    { key: "addresses", icon: MapPin, label: lang === "fr" ? "Adresses" : "Addresses" },
    { key: "wishlist", icon: Heart, label: t("nav.wishlist") },
    { key: "settings", icon: Settings, label: lang === "fr" ? "Paramètres" : "Settings" },
  ];

  const saveProfile = async (e) => {
    e.preventDefault();
    try { const { data } = await api.put("/auth/profile", profile); setUser(data.user); toast.success(t("common.save")); }
    catch (e) { toast.error(apiError(e.response?.data?.detail, lang)); }
  };
  const saveAddr = async (e) => {
    e.preventDefault();
    try { await api.post("/addresses", addr); setShowAddr(false); setAddr({ label: "Home", full_name: "", line1: "", line2: "", city: "", state: "", zip: "", phone: "", is_default: false }); api.get("/addresses").then((r) => setAddresses(r.data)); toast.success(t("common.save")); }
    catch (e) { toast.error(apiError(e.response?.data?.detail, lang)); }
  };
  const delAddr = async (id) => { await api.delete(`/addresses/${id}`); setAddresses((a) => a.filter((x) => x.id !== id)); };

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-10" data-testid="account-page">
      <h1 className="mb-8 font-display text-4xl font-bold text-navy">{lang === "fr" ? "Bonjour" : "Hello"}, {user?.name?.split(" ")[0] || ""} 👋</h1>
      <div className="grid gap-8 lg:grid-cols-[240px_1fr]">
        <aside>
          <div className="space-y-1 rounded-2xl border border-border bg-cream-card p-3 card-shadow">
            {menu.map((m) => (
              <button key={m.key} onClick={() => setParams({ tab: m.key })} data-testid={`account-tab-${m.key}`}
                className={`flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition ${tab === m.key ? "bg-navy text-gold" : "text-navy/70 hover:bg-cream"}`}>
                <m.icon size={17} /> {m.label}
              </button>
            ))}
            <button onClick={() => { logout(); navigate("/"); }} className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium text-sunset hover:bg-cream" data-testid="account-logout"><LogOut size={17} /> {t("nav.logout")}</button>
          </div>
        </aside>

        <div>
          {tab === "profile" && (
            <form onSubmit={saveProfile} className="max-w-md space-y-4 rounded-2xl border border-border bg-cream-card p-6 card-shadow" data-testid="profile-form">
              <h2 className="font-head text-lg font-bold text-navy">{lang === "fr" ? "Mon Profil" : "My Profile"}</h2>
              <div><label className="text-xs text-muted-foreground">{t("common.email")}</label><input disabled value={user?.email} className={`${inputCls} opacity-60`} /></div>
              <div><label className="text-xs text-muted-foreground">{t("common.name")}</label><input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className={inputCls} data-testid="profile-name" /></div>
              <div><label className="text-xs text-muted-foreground">{t("common.phone")}</label><input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} className={inputCls} data-testid="profile-phone" /></div>
              <button className="btn-gold rounded-full px-6 py-2.5 text-sm font-semibold" data-testid="profile-save">{t("common.save")}</button>
            </form>
          )}

          {tab === "orders" && (
            <div className="space-y-4" data-testid="orders-list">
              {orders.length === 0 ? <p className="text-muted-foreground">{lang === "fr" ? "Aucune commande." : "No orders yet."}</p> : orders.map((o) => (
                <div key={o.id} id={`order-${o.id}`} className={`rounded-2xl border bg-cream-card p-5 card-shadow transition ${highlightId === o.id ? "border-gold ring-2 ring-gold/40" : "border-border"}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="font-head font-bold text-navy">{o.order_number}</p>
                      <p className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${paymentStatusColor[o.payment_status] || "bg-gray-100"}`}>{paymentStatusLabel[o.payment_status]?.[lang] || paymentStatusLabel[o.payment_status]?.en || o.payment_status}</span>
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusColor[o.status] || "bg-gray-100"}`}>{t(`order.status.${o.status}`)}</span>
                    </div>
                    <span className="font-head font-bold text-navy">{money(o.total)}</span>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {o.items.map((it, i) => (
                      <div key={i} className="flex items-center gap-2 rounded-lg bg-cream px-2 py-1">
                        <img src={it.image} alt="" className="h-8 w-8 rounded object-cover" />
                        <span className="text-xs text-navy">{L(it, "name")} ×{it.quantity}</span>
                      </div>
                    ))}
                  </div>
                  <Link to={`/order/${o.id}`} className="mt-4 inline-block text-xs font-semibold text-ocean hover:underline" data-testid={`order-view-details-${o.id}`}>
                    {lang === "fr" ? "Voir les détails" : "View Details"}
                  </Link>
                </div>
              ))}
            </div>
          )}

          {tab === "addresses" && (
            <div className="space-y-4" data-testid="addresses-list">
              <button onClick={() => setShowAddr(!showAddr)} className="flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-sm font-semibold text-cream" data-testid="add-address-btn"><Plus size={16} /> {lang === "fr" ? "Ajouter une adresse" : "Add Address"}</button>
              {showAddr && (
                <form onSubmit={saveAddr} className="grid gap-3 rounded-2xl border border-border bg-cream-card p-5 sm:grid-cols-2" data-testid="address-form">
                  <input required placeholder={t("common.name")} value={addr.full_name} onChange={(e) => setAddr({ ...addr, full_name: e.target.value })} className={inputCls} />
                  <input required placeholder={lang === "fr" ? "Adresse" : "Address"} value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} className={inputCls} />
                  <input required placeholder={lang === "fr" ? "Ville" : "City"} value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} className={inputCls} />
                  <input required placeholder={lang === "fr" ? "État" : "State"} value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value })} className={inputCls} />
                  <input required placeholder="ZIP" value={addr.zip} onChange={(e) => setAddr({ ...addr, zip: e.target.value })} className={inputCls} />
                  <input placeholder={t("common.phone")} value={addr.phone} onChange={(e) => setAddr({ ...addr, phone: e.target.value })} className={inputCls} />
                  <button className="btn-gold rounded-full py-2.5 text-sm font-semibold sm:col-span-2">{t("common.save")}</button>
                </form>
              )}
              {addresses.map((a) => (
                <div key={a.id} className="flex items-start justify-between rounded-2xl border border-border bg-cream-card p-5 card-shadow">
                  <div className="text-sm text-navy/80"><p className="font-semibold text-navy">{a.full_name}</p><p>{a.line1}, {a.city}, {a.state} {a.zip}</p></div>
                  <button onClick={() => delAddr(a.id)} className="text-muted-foreground hover:text-sunset"><Trash2 size={17} /></button>
                </div>
              ))}
            </div>
          )}

          {tab === "wishlist" && (
            wishProducts.length === 0 ? <p className="text-muted-foreground">{lang === "fr" ? "Votre liste de favoris est vide." : "Your wishlist is empty."}</p> : (
              <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-3" data-testid="wishlist-grid">
                {wishProducts.map((p) => <ProductCard key={p.id} product={p} />)}
              </div>
            )
          )}

          {tab === "settings" && (
            <div className="max-w-md rounded-2xl border border-border bg-cream-card p-6 card-shadow">
              <h2 className="font-head text-lg font-bold text-navy">{lang === "fr" ? "Paramètres du compte" : "Account Settings"}</h2>
              <p className="mt-2 text-sm text-muted-foreground">{lang === "fr" ? "Pour changer votre mot de passe, utilisez le lien 'Mot de passe oublié'." : "To change your password, use the 'Forgot password' link."}</p>
              <Link to="/forgot-password" className="mt-4 inline-block text-sm font-semibold text-sunset hover:underline">{t("auth.forgot")}</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
